import { Navigate } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { StartupDetailsSettings } from "@/components/molecules/settings/StartupDetailsSettings";
import { BusinessPlanContextSettings } from "@/components/molecules/settings/BusinessPlanContextSettings";
import { TimelineSettings } from "@/components/molecules/settings/TimelineSettings";
import { TeamAccessSettings } from "@/components/molecules/settings/TeamAccessSettings";
import { TeamSharingPanel } from "@/components/molecules/TeamSharingPanel";
import { TeamRosterPanel } from "@/components/molecules/settings/TeamRosterPanel";
import { InvestorLinksPanel } from "@/components/molecules/settings/InvestorLinksPanel";
import { BusinessPlanSectionDraftsSettings } from "@/components/molecules/settings/BusinessPlanSectionDraftsSettings";
import { BusinessPlanPanel } from "@/components/molecules/settings/BusinessPlanPanel";
import { InvestorCommentsInbox } from "@/components/molecules/settings/InvestorCommentsInbox";
import { ApiKeysPanel } from "@/components/molecules/ApiKeysPanel";
import { BillingSettings } from "@/components/molecules/settings/BillingSettings";
import { ProUpgradeFunnel } from "@/components/molecules/ProUpgradeFunnel";
import { useProAccess } from "@/hooks/useProAccess";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import type { SettingsOutletContext } from "./SettingsLayout";

function useSettingsContext() {
  return useOutletContext<SettingsOutletContext>();
}

export function SettingsStartupSection() {
  const { settingsInitialData, isSaving, onSave, startupId } = useSettingsContext();

  return (
    <div className="space-y-6">
      <StartupDetailsSettings
        initialData={settingsInitialData}
        isLoading={isSaving}
        onSubmit={onSave}
      />
      <BusinessPlanContextSettings startupId={startupId} />
    </div>
  );
}

export function SettingsTimelineSection() {
  const { settingsInitialData, isSaving, onSave } = useSettingsContext();

  return (
    <TimelineSettings
      initialData={settingsInitialData}
      isLoading={isSaving}
      onSubmit={async (data) => onSave(data as Record<string, unknown>)}
    />
  );
}

export function SettingsTeamSection() {
  const { settingsInitialData, isSaving, isStartupOwner, startupId, ownerId, onSave } =
    useSettingsContext();

  return (
    <div className="space-y-6">
      <TeamRosterPanel startupId={startupId} />
      {isStartupOwner ? (
        <TeamAccessSettings
          initialData={settingsInitialData}
          isLoading={isSaving}
          onSubmit={onSave}
        />
      ) : null}
      <SettingsSectionCard
        title="Collaborators"
        description="Invite teammates and manage who can access this burn chart."
      >
        <TeamSharingPanel
          startupId={startupId}
          isOwner={isStartupOwner}
          ownerId={ownerId}
          teamSettings={settingsInitialData?.teamSettings}
          embedded
        />
      </SettingsSectionCard>
    </div>
  );
}

export function SettingsInvestorSection() {
  const { startupId, isStartupOwner } = useSettingsContext();

  if (!isStartupOwner) {
    return (
      <SettingsSectionCard
        title="Investor portal"
        description="Only the startup owner can manage investor links and business plans."
      >
        <p className="text-sm text-white/55">
          Ask the owner to configure investor sharing settings.
        </p>
      </SettingsSectionCard>
    );
  }

  return (
    <div className="space-y-6">
      <TeamRosterPanel startupId={startupId} />
      <InvestorLinksPanel startupId={startupId} />
      <BusinessPlanSectionDraftsSettings startupId={startupId} />
      <BusinessPlanPanel startupId={startupId} />
      <InvestorCommentsInbox startupId={startupId} />
    </div>
  );
}

export function SettingsApiSection() {
  const { isPro, isLoading } = useProAccess();

  if (isLoading) {
    return (
      <SettingsSectionCard title="API access" description="Loading access…">
        <p className="text-sm text-white/55">Checking subscription…</p>
      </SettingsSectionCard>
    );
  }

  if (!isPro) {
    return (
      <ProUpgradeFunnel
        featureId="api_access"
        variant="hero"
        title="API access is a Pro feature"
        description="Create API keys to sync burn chart data with your own tools and workflows."
      />
    );
  }

  return <ApiKeysPanel embedded />;
}

export function SettingsBillingSection() {
  return <BillingSettings />;
}

export function SettingsIndexRedirect() {
  return <Navigate to="/settings/startup" replace />;
}
